<template>
  <modal
    v-if="open"
    :title="$t('Inventory.export_title')"
    closable
    @close-request="requestClose"
  >
    <form class="inventory-export" @submit.prevent="download">
      <p class="inventory-export__help">{{ $t('Inventory.export_help') }}</p>

      <div class="inventory-export__modes" role="group" :aria-label="$t('Inventory.export_title')">
        <button
          type="button"
          :class="{'is-active': mode === modes.LOTS}"
          :aria-pressed="mode === modes.LOTS"
          :disabled="exporting"
          @click="mode = modes.LOTS"
        >
          <i class="fa fa-map-marker" aria-hidden="true" />
          {{ $t('Inventory.export_by_lot') }}
        </button>
        <button
          type="button"
          :class="{'is-active': mode === modes.BILLING}"
          :aria-pressed="mode === modes.BILLING"
          :disabled="exporting"
          @click="mode = modes.BILLING"
        >
          <i class="fa fa-user" aria-hidden="true" />
          {{ $t('Inventory.export_by_billing') }}
        </button>
      </div>

      <section v-if="mode === modes.LOTS" class="inventory-export__section">
        <small>{{ $t('Inventory.export_lots_help') }}</small>
        <label class="inventory-export__lot inventory-export__lot--all">
          <input
            type="checkbox"
            :checked="allLotsSelected"
            :disabled="exporting || !selectableLots.length"
            @change="toggleAllLots($event.target.checked)"
          >
          <span>{{ $t('Inventory.export_all_lots') }}</span>
          <em>{{ $t('Inventory.lot_vehicle_count', {count: orders.length}) }}</em>
        </label>
        <div class="inventory-export__lots">
          <label
            v-for="option in lotOptions"
            :key="option.value"
            class="inventory-export__lot"
            :class="{'is-empty': !option.count}"
          >
            <input
              v-model="selectedLots"
              type="checkbox"
              :value="option.value"
              :disabled="exporting || !option.count"
            >
            <span>{{ option.label }}</span>
            <em>{{ option.count }}</em>
          </label>
        </div>
      </section>

      <section v-else class="inventory-export__section">
        <small>{{ $t('Inventory.export_billing_help') }}</small>
        <select v-model="billingKey" class="form-control" :disabled="exporting">
          <option value="">{{ $t('Inventory.choose_billing_account') }}</option>
          <option v-for="option in billingOptions" :key="option.value" :value="option.value">
            {{ option.label || $t('Inventory.export_no_billing') }} ({{ option.count }})
          </option>
        </select>
      </section>

      <div v-if="sheets.length" class="inventory-export__summary">
        <i class="fa fa-file-excel-o" aria-hidden="true" />
        {{ $t('Inventory.export_summary', {count: exportedCount, tabs: sheets.length}) }}
      </div>

      <div v-if="feedback" class="inventory-export__error" role="alert">{{ feedback }}</div>

      <footer>
        <button type="button" class="btn btn-light" :disabled="exporting" @click="requestClose">
          {{ $t('Inventory.cancel') }}
        </button>
        <button type="submit" class="btn btn-kne-orange" :disabled="exporting || !sheets.length">
          <i :class="['fa', exporting ? 'fa-spinner fa-spin' : 'fa-download']" aria-hidden="true" />
          {{ exporting ? $t('Inventory.exporting') : $t('Inventory.export_download') }}
        </button>
      </footer>
    </form>
  </modal>
</template>

<script>
import Modal from '../../interface-components/Modal.vue'
import {
  INVENTORY_EXPORT_MODE,
  UNASSIGNED_EXPORT_LOT,
  buildInventoryExportSheets,
  downloadInventoryWorkbook,
  inventoryExportBillingOptions,
  inventoryExportColumns,
  inventoryExportFileName,
  inventoryExportLotOptions
} from '../../../../inventory/inventoryExport'

export default {
  name: 'InventoryExportModal',

  components: {
    Modal
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    orders: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      modes: INVENTORY_EXPORT_MODE,
      mode: INVENTORY_EXPORT_MODE.LOTS,
      selectedLots: [],
      billingKey: '',
      exporting: false,
      feedback: ''
    }
  },

  computed: {
    lotNames () {
      return this.$store.getters.inventoryLotNames
    },
    lotOptions () {
      return inventoryExportLotOptions(this.orders, this.lotNames).map(option => ({
        ...option,
        label: option.value === UNASSIGNED_EXPORT_LOT ? this.$t('Inventory.unassigned_lot') : option.label
      }))
    },
    selectableLots () {
      return this.lotOptions.filter(option => option.count).map(option => option.value)
    },
    allLotsSelected () {
      return Boolean(this.selectableLots.length) &&
        this.selectableLots.every(value => this.selectedLots.includes(value))
    },
    billingOptions () {
      return inventoryExportBillingOptions(this.orders)
    },
    translate () {
      return key => this.$t(key)
    },
    sheets () {
      return buildInventoryExportSheets({
        orders: this.orders,
        lotNames: this.lotNames,
        mode: this.mode,
        selectedLots: this.selectedLots,
        billingKey: this.billingKey,
        t: this.translate
      })
    },
    exportedCount () {
      return this.sheets.reduce((total, sheet) => total + sheet.orders.length, 0)
    },
    fileLabel () {
      if (this.mode === INVENTORY_EXPORT_MODE.BILLING) return this.sheets.length ? this.sheets[0].name : ''
      if (this.allLotsSelected) return ''
      return this.sheets.map(sheet => sheet.name).join('-')
    }
  },

  watch: {
    open (value) {
      if (value) this.reset()
    }
  },

  created () {
    if (this.open) this.reset()
  },

  methods: {
    reset () {
      this.mode = INVENTORY_EXPORT_MODE.LOTS
      this.selectedLots = this.selectableLots.slice()
      this.billingKey = ''
      this.feedback = ''
      this.exporting = false
    },
    toggleAllLots (checked) {
      this.selectedLots = checked ? this.selectableLots.slice() : []
    },
    async download () {
      if (this.exporting || !this.sheets.length) return
      this.exporting = true
      this.feedback = ''
      const now = Date.now()
      try {
        await downloadInventoryWorkbook({
          sheets: this.sheets,
          columns: inventoryExportColumns({t: this.translate, lotNames: this.lotNames, now}),
          fileName: inventoryExportFileName(this.fileLabel, now)
        })
        this.$emit('close')
      } catch (error) {
        console.error('[Inventory export]', error)
        this.feedback = this.$t('Inventory.export_error')
      } finally {
        this.exporting = false
      }
    },
    requestClose () {
      if (!this.exporting) this.$emit('close')
    }
  }
}
</script>

<style scoped>
.inventory-export {
  width: min(620px, 92vw);
  max-width: 100%;
  padding: 20px 24px 24px;
  color: #344250;
}

.inventory-export__help {
  margin-bottom: 14px;
  color: #6e7c89;
  font-size: .9rem;
}

.inventory-export__modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  margin-bottom: 14px;
  border-radius: 10px;
  background: #eef1f4;
}

.inventory-export__modes button {
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  color: #5d6b78;
  background: transparent;
  font-weight: 700;
}

.inventory-export__modes button.is-active {
  color: #c76a0f;
  background: #fff;
  box-shadow: 0 1px 3px rgba(40, 54, 68, .12);
}

.inventory-export__modes button i {
  margin-right: 4px;
}

.inventory-export__section {
  padding: 14px;
  border-radius: 10px;
  background: #f4f6f8;
}

.inventory-export__section > small {
  display: block;
  margin-bottom: 10px;
  color: #7b8793;
}

.inventory-export__lots {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 6px;
  max-height: 260px;
  overflow-y: auto;
}

.inventory-export__lot {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px 8px;
  margin: 0;
  border: 1px solid #e0e5ea;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.inventory-export__lot span {
  overflow: hidden;
  flex: 1;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-export__lot em {
  color: #7b8793;
  font-size: .78rem;
  font-style: normal;
}

.inventory-export__lot.is-empty {
  cursor: default;
  opacity: .5;
}

.inventory-export__lot--all {
  margin-bottom: 8px;
  border-color: #f0c08f;
}

.inventory-export__summary {
  margin-top: 12px;
  color: #23633c;
  font-size: .88rem;
}

.inventory-export__summary i {
  margin-right: 4px;
}

.inventory-export__error {
  padding: 10px 12px;
  margin-top: 12px;
  border-radius: 8px;
  color: #8d2f32;
  background: #fde7e7;
}

.inventory-export footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e6eaee;
}

@media (max-width: 600px) {
  .inventory-export {
    width: 100%;
    padding: 16px;
  }

  .inventory-export footer {
    flex-direction: column-reverse;
  }

  .inventory-export footer .btn {
    width: 100%;
  }
}
</style>
